import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { Card } from 'components/Card';
import { CardGeneric } from 'components/CardGeneric';
import { CardRow } from 'components/CardRow';
import {
  ModalScreens,
  NavigationStacks,
  Screens,
  Stacks,
} from 'components/Navigation/types';
import { Page } from 'components/Page';
import { Txt } from 'components/Txt';
import { useScreenSize } from 'hooks/useScreenSize';
import { useSelector } from 'hooks/useSelector';
import { pluralise } from 'lib/pluralise';
import { filterArrayEmpty } from 'lib/riskbase';
import { isEqual } from 'lodash';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL } from 'store/actions';
import {
  selectAseIsEditable,
  selectAseIsOnline,
  selectAseSection,
  selectAseSectionActionIds,
  selectAseSectionControlIds,
  selectAseTemplateSettings,
  selectAseTplSection,
} from 'store/assessments/selectors';
import { AddCustomAction } from '../Explorer/ExplorerActionRow';
import { AddCustomControl } from '../Explorer/ExplorerControlRow';
import { PageHeader } from 'components/PageHeader';
import { View } from 'react-native';
import { GapSmall } from 'components/GapSmall';

type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.ASSESSMENT],
  Screens.ASE_SECTION
>;

export const SectionDisplay: React.FC<Props> = ({ navigation, route }) => {
  const dispatch = useDispatch();
  const screenSize = useScreenSize();
  const { id } = route.params;

  const editable = useSelector(selectAseIsEditable);
  const isOnline = useSelector(selectAseIsOnline);
  const isQc = editable && isOnline;

  const template = useSelector((state) => selectAseTplSection(state, id));
  const result = useSelector((state) => selectAseSection(state, id));
  const templateSettings = useSelector(selectAseTemplateSettings);

  const actionsCount = useSelector(
    (state) => selectAseSectionActionIds(state, id),
    isEqual,
  ).length;
  const controlsCount = useSelector(
    (state) => selectAseSectionControlIds(state, id),
    isEqual,
  ).length;

  const handleEditPress = () =>
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.AseSection,
        params: {
          id,
        },
      }),
    );

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title="Section"
          saveButtonText="Edit"
          onPressBackButton={
            screenSize === 'small' ? navigation.goBack : undefined
          }
          onPressSaveButton={isQc ? handleEditPress : undefined}
        />
      }
    >
      <CardGeneric>
        <Txt size="large" weight="bold">
          {template.name}
        </Txt>
        {!!template.description && <Txt>{template.description}</Txt>}
      </CardGeneric>

      <Card
        explanation={
          (!!actionsCount || !!controlsCount) &&
          `${[
            pluralise(actionsCount, '1 action', '{x} actions', ''),
            pluralise(controlsCount, '1 control', '{x} controls', ''),
          ]
            .filter(filterArrayEmpty)
            .join(' and ')} added to section`
        }
      >
        <CardRow
          labelAlignTop
          label="Remarks"
          value={result?.comment}
          placeholder="No Remarks"
        />
      </Card>

      {isQc &&
        (templateSettings.addCustomActions ||
          templateSettings.addCustomControls) && (
          <View>
            <Card
              label={`Add ${[
                templateSettings.addCustomActions ? 'Actions' : null,
                templateSettings.addCustomControls ? 'Controls' : null,
              ]
                .filter(filterArrayEmpty)
                .join(' and ')}`}
            />
            <GapSmall>
              {templateSettings.addCustomActions && (
                <Card>
                  <AddCustomAction
                    parentId={id}
                    parentType="section"
                    fieldInstanceId={null}
                    parent={null}
                  />
                </Card>
              )}
              {templateSettings.addCustomControls && (
                <Card>
                  <AddCustomControl
                    parentId={id}
                    parentType="section"
                    fieldInstanceId={null}
                    parent={null}
                  />
                </Card>
              )}
            </GapSmall>
          </View>
        )}
    </Page>
  );
};
